import { makeStyles, Spinner, Tab, TabList, tokens } from '@fluentui/react-components';
import React, { useState } from 'react';
import { useProject } from '../../context/ProjectContext';
import ProjektmanagementArbeitspakete from './ProjektmanagementArbeitspakete';
import ProjektmanagementBerichte from './ProjektmanagementBerichte';
import ProjektmanagementMeilensteine from './ProjektmanagementMeilensteine';
import ProjektmanagementProjektstunden from './ProjektmanagementProjektstunden';

const useStyles = makeStyles({
    container: {
        padding: `0 ${tokens.spacingHorizontalL}`,
        height: '100%',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        paddingTop: tokens.spacingVerticalL,
        borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
        marginBottom: tokens.spacingVerticalL,
    },
    title: {
        margin: 0,
        marginBottom: tokens.spacingVerticalM,
        fontSize: tokens.fontSizeHero800,
    },
    content: {
        flex: 1,
        overflow: 'auto',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        minHeight: '100px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: 'center',
        margin: '10px',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        minHeight: '300px',
    },
    errorContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        minHeight: '300px',
    },
});

type ProjektmanagementTab = 'arbeitspakete' | 'meilensteine' | 'berichte' | 'projektstunden';

const Projektmanagement: React.FC = () => {
    const styles = useStyles();
    const { project, isLoading, error } = useProject();
    const [selectedTab, setSelectedTab] = useState<ProjektmanagementTab>('arbeitspakete');

    const renderContent = () => {
        switch (selectedTab) {
            case 'arbeitspakete':
                return <ProjektmanagementArbeitspakete project={project} />;
            case 'meilensteine':
                return <ProjektmanagementMeilensteine project={project} />;
            case 'berichte':
                return <ProjektmanagementBerichte project={project} />;
            case 'projektstunden':
                return <ProjektmanagementProjektstunden project={project} />;
            default:
                return <ProjektmanagementArbeitspakete project={project} />;
        }
    };

    if (isLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading Project..." labelPosition="below" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.errorContainer}>
                    <h2 style={{ color: tokens.colorNeutralForeground3 }}>
                        {error}
                    </h2>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>{project?.title || 'Project Details'}</h1>
                <TabList
                    selectedValue={selectedTab}
                    onTabSelect={(_event, data) => setSelectedTab(data.value as ProjektmanagementTab)}
                >
                    <Tab value="arbeitspakete">Work packages</Tab>
                    <Tab value="meilensteine">Milestones</Tab>
                    <Tab value="berichte">Reports</Tab>
                    <Tab value="projektstunden">Project hours</Tab>
                </TabList>
            </div>
            <div className={styles.content}>
                {renderContent()}
            </div>
        </div>
    );
};

export default Projektmanagement;
